import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Inline, Text } from '@atlaskit/primitives';

import { TableRows, TableHeads } from '@/common/fastshift-table';
import { useRoutes } from '@/common/hooks';
import { FASTSHIFT_PATHS, FASTSHIFT_PATH_KEYS } from '@/common/routes/constants';
import { useFastshiftSelector } from '@/state';

import { useCustomerSelectionUIAnalytics } from '../hooks';
import { messages } from '../messages';

const DEFAULT_PAGINATION = {
	page: 0,
	size: 20,
	totalElements: 0,
	totalPages: 0,
	hasNext: false,
	hasPrevious: false,
	sorted: false,
};

export const useDomainSelectionTable = () => {
	const { getPathByDomainId } = useRoutes();
	const {
		pending: customerDomainsPending,
		data: customerDomainsData,
		error: customerDomainsError,
	} = useFastshiftSelector((state) => state.customerDomains);

	const navigate = useNavigate();
	const { sendSeeDetailsClickUIEvent } = useCustomerSelectionUIAnalytics();

	if (!customerDomainsData)
		return {
			isTableLoading: customerDomainsPending,
			isTableError: !!customerDomainsError,
			rows: [] as TableRows,
			head: { cells: [] } as TableHeads,
			pagination: DEFAULT_PAGINATION,
		};

	const head: TableHeads = {
		cells: [
			{
				key: 'customerName',
				content: (
					<Heading size="xxsmall">
						<FormattedMessage {...messages.customerColumnTitle} />
					</Heading>
				),
				width: 40,
				isSortable: true,
			},
			{
				key: 'customerDomain',
				content: (
					<Heading size="xxsmall">
						<FormattedMessage {...messages.customerDomainColumnTitle} />
					</Heading>
				),
				width: 40,
				isSortable: true,
			},
			{
				key: 'actions',
				content: (
					<Inline alignInline="end">
						<Heading size="xxsmall">
							<FormattedMessage {...messages.actionsColumnTitle} />
						</Heading>
					</Inline>
				),
				width: 20,
			},
		],
	};

	const rows: TableRows = customerDomainsData.data.map((customer) => {
		const domainScopedOverviewPath = getPathByDomainId(
			FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.OVERVIEW],
			customer.customerDomainId || '',
		);

		return {
			key: customer.customerDomainId,
			cells: [
				{
					key: 'customerName',
					content: (
						<Inline space="space.100">
							<Image
								src={customer.customerLogoUrl}
								alt={`${customer.customerName} logo`}
								style={{
									width: '20px',
									height: '20px',
									borderRadius: '3px',
								}}
							/>
							<Text size="medium">{customer.customerName}</Text>
						</Inline>
					),
				},
				{
					key: 'customerDomain',
					content: (
						<Inline space="space.100">
							<Text>{customer.customerDomain}</Text>
						</Inline>
					),
				},
				{
					key: 'actions',
					content: (
						<Inline alignInline="end">
							<Button
								appearance="default"
								spacing="compact"
								isDisabled={!domainScopedOverviewPath}
								onClick={() => {
									if (domainScopedOverviewPath) {
										sendSeeDetailsClickUIEvent(customer.customerDomain, customer.customerDomainId);
										navigate(domainScopedOverviewPath);
									}
								}}
							>
								<FormattedMessage {...messages.seeDetailsButton} />
							</Button>
						</Inline>
					),
				},
			],
		};
	});

	return {
		isTableLoading: customerDomainsPending,
		isTableError: !!customerDomainsError,
		rows,
		head,
		pagination: customerDomainsData.pagination,
	};
};
